import type { ChecklistItem, ChecklistResponse, HandoverDraft } from './types';

function hasAnswer(answer: ChecklistResponse['answer'] | undefined): boolean {
  return answer === 'sim' || answer === 'nao';
}

export function isItemComplete(
  _item: ChecklistItem,
  response: ChecklistResponse | undefined,
): boolean {
  return !!response && hasAnswer(response.answer);
}

/**
 * Item está "safely answered" quando o operador respondeu com a resposta
 * esperada (safe_answer). Item não respondido não conta como safe — é incompleto.
 */
export function isItemSafelyAnswered(
  item: ChecklistItem,
  response: ChecklistResponse | undefined,
): boolean {
  if (!response || !hasAnswer(response.answer)) return false;
  return response.answer === item.safe_answer;
}

/**
 * Item em DIVERGÊNCIA: foi respondido mas a resposta diverge da esperada.
 * Item incompleto não é divergência.
 */
export function itemHasDivergence(
  item: ChecklistItem,
  response: ChecklistResponse | undefined,
): boolean {
  if (!response || !hasAnswer(response.answer)) return false;
  return !isItemSafelyAnswered(item, response);
}

export function divergentItems(
  items: ChecklistItem[],
  responses: ChecklistResponse[],
): ChecklistItem[] {
  const responseByItem = new Map(responses.map((r) => [r.item_id, r]));
  return items.filter((item) => itemHasDivergence(item, responseByItem.get(item.id)));
}

export function isDraftComplete(items: ChecklistItem[], draft: HandoverDraft): boolean {
  const activeItems = items.filter((i) => i.is_active);
  if (activeItems.length === 0) return false;

  const responseByItem = new Map(draft.responses.map((r) => [r.item_id, r]));
  const allItemsAnswered = activeItems.every((item) =>
    isItemComplete(item, responseByItem.get(item.id)),
  );
  if (!allItemsAnswered) return false;

  const hasDivergence = activeItems.some((item) =>
    itemHasDivergence(item, responseByItem.get(item.id)),
  );
  if (hasDivergence && !hasObservation(draft.observation)) return false;

  return true;
}

export function hasObservation(observation: string | null | undefined): boolean {
  return typeof observation === 'string' && observation.trim().length > 0;
}

export function observationRequired(
  items: ChecklistItem[],
  responses: ChecklistResponse[],
): boolean {
  return divergentItems(items, responses).length > 0;
}

export function countItemsCompleted(items: ChecklistItem[], responses: ChecklistResponse[]): number {
  const responseByItem = new Map(responses.map((r) => [r.item_id, r]));
  return items.filter((item) => isItemComplete(item, responseByItem.get(item.id))).length;
}

/**
 * Conta divergências percorrendo as RESPOSTAS (não o catálogo), resolvendo cada
 * item por `itemById`. Uma resposta cujo item saiu do catálogo (zombie) conta
 * como divergência — o auditor precisa ver que o contexto da pergunta foi
 * perdido. Consolida a lógica antes duplicada inline em AuditRow e
 * HistoricoTrocasPage (GUPM-147).
 */
export function countDivergencesFromResponses(
  responses: ChecklistResponse[],
  itemById: Map<string, ChecklistItem>,
): number {
  return responses.filter((response) => {
    const item = itemById.get(response.item_id);
    if (!item) return true; // zombie: item removido do catálogo
    return itemHasDivergence(item, response);
  }).length;
}
